import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import gsap from 'gsap'
import {
  CURTAIN,
  DURATION,
  EASE,
  armCurtain,
  pageCurtain,
  prefersReducedMotion,
  refreshScrollTriggers,
} from '../../animations/gsapAnimations'

/* Enter-only, deliberately. A true cross-fade means holding the outgoing
   page mounted while the incoming one loads, which delays navigation — the
   one thing the animation law says never to do. So the old page leaves the
   instant the route changes, and what covers the swap is a curtain: a
   black panel already down over the new page, lifting from the top with a
   lime edge, while the page beneath rises to meet it. The visitor sees the
   masthead first, the way the room's lights come up on the car first.

   The first page of a session gets no curtain — the hero has its own
   arrival, and a curtain over it would be a curtain over nothing. */
function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const ref = useRef<HTMLDivElement>(null)
  const curtainRef = useRef<HTMLDivElement>(null)
  const mounted = useRef(false)

  /* The curtain has to be armed before the new page's own effects run, and
     a child's layout effect runs before this component's. So the clock is
     set during the render that changes the path — the one moment that is
     guaranteed to come first — using the adjust-state-during-render
     pattern rather than a ref, which the compiler will not let a render
     read. */
  const [seen, setSeen] = useState(pathname)
  if (seen !== pathname) {
    setSeen(pathname)
    if (!prefersReducedMotion()) armCurtain(CURTAIN.holdMs)
  }

  useLayoutEffect(() => {
    const el = ref.current
    const curtain = curtainRef.current
    if (!el || !curtain) return

    const first = !mounted.current
    mounted.current = true

    if (prefersReducedMotion()) {
      refreshScrollTriggers()
      return
    }

    const ctx = gsap.context(() => {
      if (first) {
        gsap.fromTo(
          el,
          { opacity: 0, y: 12 },
          {
            opacity: 1,
            y: 0,
            duration: DURATION.fast,
            ease: EASE,
            clearProps: 'transform',
            /* Positions measured mid-tween are wrong; recompute once still. */
            onComplete: refreshScrollTriggers,
          },
        )
        return
      }

      pageCurtain(curtain, el).eventCallback('onComplete', refreshScrollTriggers)
    })

    return () => ctx.revert()
  }, [pathname])

  return (
    <>
      <div ref={ref}>{children}</div>
      {/* The curtain lives beside the page, not inside it: the page is
          transformed as it rises, and a fixed element inside a transformed
          ancestor is no longer fixed to the viewport. */}
      <div
        ref={curtainRef}
        aria-hidden="true"
        className="pointer-events-none invisible fixed inset-0 z-55 bg-ink"
      >
        <span className="absolute inset-x-0 top-0 h-px bg-lime shadow-[0_0_28px_rgb(200_245_66/0.85),0_0_6px_rgb(200_245_66)]" />
        <span className="absolute inset-x-0 top-0 h-40 bg-linear-to-b from-lime/10 to-transparent" />
      </div>
    </>
  )
}

export default PageTransition
